import { FC, ReactNode, useMemo, useState } from 'react';
import { Dropdown, Menu } from 'antd';
import { TbDatabase, TbDatabaseCog, TbDatabasePlus, TbRefresh } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import {
    BaseSkeleton,
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    LearnMoreFooter,
} from '@/shared';
import AddDataSourceModal from '@/shared/components/modals/ResourceCreationModals/AddDataSourceModal';
import { useConnectedAccountStore } from '@/stores';
import { imgLogoStyling } from '@/theme';
import { ConnectedAccount, ConnectedAccountType } from '@/types';
import { formatDate } from '@/utilities';

export interface DataSourceDropdownOptionState {
    disabled?: boolean;
    metadata?: ReactNode[];
    selected?: boolean;
}

interface DataSourcesDropdownProps {
    children: ReactNode;
    getOptionState?: (dataSource: ConnectedAccount) => DataSourceDropdownOptionState;
    onSelectDataSourceHandler?: (dataSource: ConnectedAccount) => void;
    width?: string | number;
}

const DataSourcesDropdown: FC<DataSourcesDropdownProps> = ({
    children,
    getOptionState,
    onSelectDataSourceHandler,
    width,
}): JSX.Element => {
    const [activeView, setActiveView] = useState<'add' | 'manage'>('add');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [hasRequestedConnectedAccounts, setHasRequestedConnectedAccounts] = useState(false);
    const [search, setSearch] = useState('');

    const { connectedAccounts, fetchConnectedAccounts, loading: connectedAccountsLoading } = useConnectedAccountStore();

    const { token } = useAntDesignTheme();

    const [addDataSourceModalOpen, setAddDataSourceModalOpen] = useState(false);

    const { integrationLabelToIconMap } = useMaps();

    const showConnectedAccountsLoading = connectedAccountsLoading && connectedAccounts.length === 0;
    const filteredConnectedAccounts = useMemo(() => {
        const normalizedSearch = search.trim().toLocaleLowerCase();

        if (!normalizedSearch) {
            return connectedAccounts;
        }

        return connectedAccounts.filter((dataSource) =>
            [dataSource.nickname, dataSource.type].some((field) =>
                field?.toLocaleLowerCase().includes(normalizedSearch),
            ),
        );
    }, [connectedAccounts, search]);

    const handleDropdownOpenChange = (nextOpen: boolean) => {
        setDropdownOpen(nextOpen);

        if (!nextOpen) {
            setSearch('');
        }

        if (nextOpen && !hasRequestedConnectedAccounts && connectedAccounts.length === 0) {
            setHasRequestedConnectedAccounts(true);
            void fetchConnectedAccounts();
        }
    };

    return (
        <>
            <Dropdown
                open={dropdownOpen}
                onOpenChange={handleDropdownOpenChange}
                trigger={['click']}
                placement='bottomRight'
                popupRender={() => (
                    <DropdownPanel
                        actions={
                            <DropdownPanelMenu selectable={false}>
                                <Menu.Item
                                    key='add'
                                    onClick={() => {
                                        setActiveView('add');
                                        setAddDataSourceModalOpen(true);
                                        setDropdownOpen(false);
                                    }}
                                >
                                    <DropdownOptionContent
                                        singleLine
                                        title='Add Data Source'
                                        titlePrefix={
                                            <TbDatabasePlus size='1rem' style={{ color: token.colorTextTertiary }} />
                                        }
                                    />
                                </Menu.Item>
                                <Menu.Item
                                    key='manage'
                                    onClick={() => {
                                        setActiveView('manage');
                                        setAddDataSourceModalOpen(true);
                                        setDropdownOpen(false);
                                    }}
                                >
                                    <DropdownOptionContent
                                        singleLine
                                        title='Manage Data Sources'
                                        titlePrefix={
                                            <TbDatabaseCog size='1rem' style={{ color: token.colorTextTertiary }} />
                                        }
                                    />
                                </Menu.Item>
                            </DropdownPanelMenu>
                        }
                        footer={<LearnMoreFooter topic='Data Sources' variant='dropdown' />}
                        search={{
                            placeholder: 'Search Data Sources...',
                            setValue: setSearch,
                            value: search,
                        }}
                        style={{ marginTop: '0.5rem' }}
                        width={width ?? 16 * 28}
                    >
                        <DropdownMenuLabel icon={<TbDatabase size='0.875rem' />}>
                            {filteredConnectedAccounts.length} Data Source
                            {filteredConnectedAccounts.length === 1 ? '' : 's'}
                        </DropdownMenuLabel>
                        <DropdownPanelMenu selectable={false}>
                            {showConnectedAccountsLoading ? (
                                <Menu.Item disabled key='loading-data-sources'>
                                    <BaseSkeleton title={false} paragraph={{ rows: 3 }} />
                                </Menu.Item>
                            ) : filteredConnectedAccounts.length ? (
                                filteredConnectedAccounts.map((dataSource) => {
                                    const optionState = getOptionState?.(dataSource);

                                    return (
                                        <Menu.Item
                                            disabled={optionState?.disabled}
                                            key={dataSource.id}
                                            onClick={() => {
                                                onSelectDataSourceHandler?.(dataSource);
                                                setDropdownOpen(false);
                                            }}
                                        >
                                            <DropdownOptionContent
                                                avatar={{
                                                    icon: (
                                                        <img
                                                            alt={dataSource.type}
                                                            src={
                                                                integrationLabelToIconMap[
                                                                    dataSource.type as ConnectedAccountType
                                                                ]
                                                            }
                                                            style={{
                                                                ...imgLogoStyling,
                                                                transform: 'scale(0.75)',
                                                            }}
                                                        />
                                                    ),
                                                }}
                                                metadata={[
                                                    <span key='last-synced'>
                                                        <TbRefresh
                                                            style={{
                                                                marginRight: '0.25rem',
                                                                verticalAlign: '-0.125rem',
                                                            }}
                                                        />
                                                        Last synced{' '}
                                                        {dataSource.updatedAt && formatDate(dataSource.updatedAt)}
                                                    </span>,
                                                    ...(optionState?.metadata ?? []),
                                                ]}
                                                selected={optionState?.selected}
                                                title={dataSource.nickname ?? dataSource.type}
                                            />
                                        </Menu.Item>
                                    );
                                })
                            ) : (
                                <EmptySection
                                    height='12.5rem'
                                    icon={<TbDatabase size='75%' style={{ color: token.colorTextTertiary }} />}
                                    label='No Data Sources'
                                    description={
                                        search
                                            ? 'No data sources match your search.'
                                            : 'Add and manage all of your connected data sources from here.'
                                    }
                                />
                            )}
                        </DropdownPanelMenu>
                    </DropdownPanel>
                )}
            >
                <span>{children}</span>
            </Dropdown>
            <AddDataSourceModal
                open={addDataSourceModalOpen}
                setOpen={setAddDataSourceModalOpen}
                activeView={activeView}
            />
        </>
    );
};

export default DataSourcesDropdown;
